import React, { useEffect, useState, useRef } from 'react';
import { runStatsigAutoCapture } from '@statsig/web-analytics';
import { runStatsigSessionReplay } from '@statsig/session-replay';
import { StatsigProvider, useClientAsyncInit } from '@statsig/react-bindings';
import { getEnvironment } from '../Common';
import { bindStatsigClient } from './logEvent';

// Statsig client key for the borrower-facing app, which has its own project.
//
// It is deliberately NOT the LO app's project. Session replay privacy is a
// project-level setting, and this app records 1003 forms where borrowers type
// SSN, date of birth and income. Its project is set to mask all inputs; the LO
// project only masks passwords, which is fine for an internal back office but
// not for this. Repointing this at the LO key would silently undo that.
//
// Client keys are public: they ship in the bundle.
const STATSIG_CLIENT_KEY = "client-csHfASbZBtcIvQl2jAnb0hMDTZ3kNpy72eqrG3Rg5bd";

const StatsigWrapper = React.memo(({ children }) => {
    const initRef = useRef(false);
    const [ready, setReady] = useState(false);

    const { client: statsigClient } = useClientAsyncInit(
        STATSIG_CLIENT_KEY,
        {
            userAgent: navigator.userAgent,
            language: navigator.language,
            custom: {
                origin: window.location.origin || "",
            }
        },
        {
            environment: {
                tier: getEnvironment()
            }
        }
    );

    useEffect(() => {
        if (!statsigClient || initRef.current) return;

        const initializeStatsig = async () => {
            if (statsigClient.loadingStatus === 'Ready' && !initRef.current) {
                try {
                    console.log("Statsig initializing...");
                    initRef.current = true;

                    const context = statsigClient.getContext();
                    const derivedFields = context?.values?.derived_fields;
                    const savedUserInfo = sessionStorage.getItem('statsig_user_info');

                    if (savedUserInfo) {
                        const userInfo = JSON.parse(savedUserInfo);
                        await statsigClient.updateUserAsync({
                            ...userInfo,
                            customIDs: {
                                ...(userInfo?.customIDs || {}),
                                IP: derivedFields?.ip || '',
                            }
                        });
                    } else {
                        await statsigClient.updateUserAsync({
                            customIDs: {
                                IP: derivedFields?.ip || '',
                            }
                        });
                    }

                    runStatsigAutoCapture(statsigClient);
                    runStatsigSessionReplay(statsigClient);
                    bindStatsigClient(statsigClient);

                    setReady(true);
                    console.log("Statsig initialized successfully");
                } catch (error) {
                    console.error('Error initializing Statsig:', error);
                    initRef.current = false;
                    // Still render the app if Statsig init fails.
                    setReady(true);
                }
            }
        };

        initializeStatsig();
    }, [statsigClient?.loadingStatus]);

    // Wait until bind completes before mounting routes. Previously we rendered
    // bare children then swapped in StatsigProvider, which remounted the tree
    // and double-fired PAGE_VIEW (queue flush + second mount).
    if (!statsigClient || !ready) {
        return null;
    }

    return (
        <StatsigProvider client={statsigClient}>
            {children}
        </StatsigProvider>
    );
});

StatsigWrapper.displayName = 'StatsigWrapper';

export default StatsigWrapper;
